import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import useInactivityLogout from './components/InactivityLogout';
import { useLanguage } from './context/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import SkipLink from './components/SkipLink';
import CookieConsent from './components/CookieConsent';
import AdminLayout from './components/AdminLayout';
import Favicon from './components/Favicon';
import DocumentTitle from './components/DocumentTitle';
import Home from './pages/Home';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Login from './pages/Login';
import Login2FA from './pages/Login2FA';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Register from './pages/Register';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import MyAccount from './pages/MyAccount';
import MyFavorites from './pages/MyFavorites';
import Contact from './pages/Contact';
import AdminDashboard from './pages/AdminDashboard';
import AdminProducts from './pages/AdminProducts';
import AdminOrders from './pages/AdminOrders';
import AdminOrderDetail from './pages/AdminOrderDetail';
import AdminRMACreate from './pages/AdminRMACreate';
import AdminRefundCreate from './pages/AdminRefundCreate';
import AdminStoreCredits from './pages/AdminStoreCredits';
import AdminOrderReports from './pages/AdminOrderReports';
import AdminStoreSettings from './pages/AdminStoreSettings';
import AdminFinanceReports from './pages/AdminFinanceReports';
import AdminInvoices from './pages/AdminInvoices';
import AdminInvoiceView from './pages/AdminInvoiceView';
import AdminInvoiceSettings from './pages/AdminInvoiceSettings';
import AdminCreditInvoices from './pages/AdminCreditInvoices';
import AdminCreditInvoiceView from './pages/AdminCreditInvoiceView';
import AdminPaymentMethods from './pages/AdminPaymentMethods';
import AdminShippingMethods from './pages/AdminShippingMethods';
import AdminCourierIntegrations from './pages/AdminCourierIntegrations';
import AdminCategories from './pages/AdminCategories';
import AdminCoupons from './pages/AdminCoupons';
import AdminPromotions from './pages/AdminPromotions';
import AdminLoyalty from './pages/AdminLoyalty';
import AdminGiftCards from './pages/AdminGiftCards';
import AdminEmailAutomation from './pages/AdminEmailAutomation';
import AdminPages from './pages/AdminPages';
import AdminPageEditor from './pages/AdminPageEditor';
import AdminHomePageEditor from './pages/AdminHomePageEditor';
import AdminFooterLinks from './pages/AdminFooterLinks';
import AdminHeaderMenu from './pages/AdminHeaderMenu';
import PageView from './pages/PageView';
import AdminMarketplaceIntegrations from './pages/AdminMarketplaceIntegrations';
import AdminBackups from './pages/AdminBackups';
import AdminErrorLogs from './pages/AdminErrorLogs';
import AdminSmtpSettings from './pages/AdminSmtpSettings';
import AdminUsers from './pages/AdminUsers';
import PaymentStripe from './pages/PaymentStripe';
import PaymentStripeReturn from './pages/PaymentStripeReturn';
import PaymentPayPalReturn from './pages/PaymentPayPalReturn';
import PaymentPayPalCancel from './pages/PaymentPayPalCancel';
import MaintenanceGate from './components/MaintenanceGate';
import './App.css';

function App() {
  useInactivityLogout();
  const { language } = useLanguage();

  // Parodyti turinį po to, kai React ir CSS jau įkelti – išvengiama FOUC
  useEffect(() => {
    const showRoot = () => {
      const root = document.getElementById('root');
      if (root) root.style.opacity = '1';
    };
    requestAnimationFrame(() => requestAnimationFrame(showRoot));
  }, []);

  return (
    <Router>
      <Favicon />
      <DocumentTitle />
      <div className="App">
        <Routes>
          {/* Public routes with Header (MaintenanceGate: offline režime išoriniai mato pranešimą, admin – pilną turinį) */}
          <Route path="/*" element={
            <MaintenanceGate>
              <>
                <SkipLink />
                <Header />
                <main id="main-content" tabIndex={-1} key={language || 'lt'}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/products" element={<Products />} />
                    <Route path="/products/:slug" element={<ProductDetail />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/login-2fa" element={<Login2FA />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                    <Route path="/verify-email/:token" element={<VerifyEmail />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/orders" element={<Orders />} />
                    <Route path="/orders/:id" element={<OrderDetail />} />
                    <Route path="/my-account" element={<MyAccount />} />
                    <Route path="/my-favorites" element={<MyFavorites />} />
                    <Route path="/payment/stripe" element={<PaymentStripe />} />
                    <Route path="/payment/stripe/return" element={<PaymentStripeReturn />} />
                    <Route path="/payment/stripe/cancel" element={<Navigate to="/cart" replace />} />
                    <Route path="/payment/paypal/return" element={<PaymentPayPalReturn />} />
                    <Route path="/payment/paypal/cancel" element={<PaymentPayPalCancel />} />
                    <Route path="/page/:slug" element={<PageView />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/cookies" element={<Navigate to="/page/cookies" replace />} />
                  </Routes>
                </main>
                <Footer />
                <div className="footer-bg-fill" aria-hidden="true" />
                <CookieConsent />
              </>
            </MaintenanceGate>
          } />
          
          {/* Admin routes with AdminLayout (no Header) */}
          <Route path="/admin" element={<AdminLayout><AdminDashboard /></AdminLayout>} />
          <Route path="/admin/products" element={<AdminLayout><AdminProducts /></AdminLayout>} />
          <Route path="/admin/orders" element={<AdminLayout><AdminOrders /></AdminLayout>} />
          <Route path="/admin/orders/:id" element={<AdminLayout><AdminOrderDetail /></AdminLayout>} />
          <Route path="/admin/orders/:id/return" element={<AdminLayout><AdminRMACreate /></AdminLayout>} />
          <Route path="/admin/orders/:id/refund" element={<AdminLayout><AdminRefundCreate /></AdminLayout>} />
          <Route path="/admin/orders/reports" element={<AdminLayout><AdminOrderReports /></AdminLayout>} />
          <Route path="/admin/store-credits" element={<AdminLayout><AdminStoreCredits /></AdminLayout>} />
          <Route path="/admin/store-settings" element={<AdminLayout><AdminStoreSettings /></AdminLayout>} />
          <Route path="/admin/finance-reports" element={<AdminLayout><AdminFinanceReports /></AdminLayout>} />
          <Route path="/admin/invoices" element={<AdminLayout><AdminInvoices /></AdminLayout>} />
          <Route path="/admin/invoices/:id" element={<AdminLayout><AdminInvoiceView /></AdminLayout>} />
          <Route path="/admin/invoice-settings" element={<AdminLayout><AdminInvoiceSettings /></AdminLayout>} />
          <Route path="/admin/credit-invoices" element={<AdminLayout><AdminCreditInvoices /></AdminLayout>} />
          <Route path="/admin/credit-invoices/:id" element={<AdminLayout><AdminCreditInvoiceView /></AdminLayout>} />
          <Route path="/admin/payment-methods" element={<AdminLayout><AdminPaymentMethods /></AdminLayout>} />
          <Route path="/admin/shipping-methods" element={<AdminLayout><AdminShippingMethods /></AdminLayout>} />
          <Route path="/admin/courier-integrations" element={<AdminLayout><AdminCourierIntegrations /></AdminLayout>} />
          <Route path="/admin/categories" element={<AdminLayout><AdminCategories /></AdminLayout>} />
          <Route path="/admin/coupons" element={<AdminLayout><AdminCoupons /></AdminLayout>} />
          <Route path="/admin/promotions" element={<AdminLayout><AdminPromotions /></AdminLayout>} />
          <Route path="/admin/loyalty" element={<AdminLayout><AdminLoyalty /></AdminLayout>} />
          <Route path="/admin/gift-cards" element={<AdminLayout><AdminGiftCards /></AdminLayout>} />
          <Route path="/admin/email-automation" element={<AdminLayout><AdminEmailAutomation /></AdminLayout>} />
          <Route path="/admin/pages" element={<AdminLayout><AdminPages /></AdminLayout>} />
          <Route path="/admin/pages/new" element={<AdminLayout><AdminPageEditor /></AdminLayout>} />
          <Route path="/admin/pages/:id/edit" element={<AdminLayout><AdminPageEditor /></AdminLayout>} />
          <Route path="/admin/home-page-editor" element={<AdminLayout><AdminHomePageEditor /></AdminLayout>} />
          <Route path="/admin/footer-links" element={<AdminLayout><AdminFooterLinks /></AdminLayout>} />
          <Route path="/admin/header-menu" element={<AdminLayout><AdminHeaderMenu /></AdminLayout>} />
          <Route path="/admin/marketplace-integrations" element={<AdminLayout><AdminMarketplaceIntegrations /></AdminLayout>} />
          <Route path="/admin/backups" element={<AdminLayout><AdminBackups /></AdminLayout>} />
          <Route path="/admin/error-logs" element={<AdminLayout><AdminErrorLogs /></AdminLayout>} />
          <Route path="/admin/smtp-settings" element={<AdminLayout><AdminSmtpSettings /></AdminLayout>} />
          <Route path="/admin/users" element={<AdminLayout><AdminUsers /></AdminLayout>} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
